import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useLanguage } from '@/contexts/LanguageContext';
import { Instagram, Check, Loader2, Lock } from 'lucide-react';
import { extractInstagramUsername, validateInstagramInput, isValidInstagramUsername } from '@/utils/instagramUsername';
import { logEdgeFunctionError } from '@/utils/sentry';

export const InstagramProfileSettings = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { t } = useLanguage();
  const [instagramUsername, setInstagramUsername] = useState('');
  const [currentUsername, setCurrentUsername] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(true);
  const [validationError, setValidationError] = useState('');

  useEffect(() => {
    const fetchProfile = async () => {
      if (!user?.id) {
        console.warn('[InstagramProfileSettings] No user ID available');
        return;
      }


      try {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('instagram_username, user_id')
          .eq('user_id', user.id)
          .maybeSingle();

        if (error) {
          console.error('[InstagramProfileSettings] Error fetching profile:', error);
          throw error;
        }


        // Security check: Ensure fetched data belongs to current user
        if (data && data.user_id !== user.id) {
          console.error('[InstagramProfileSettings] Data leak detected! Fetched data belongs to different user');
          toast({
            title: t('common.error'),
            description: 'Security error: Data mismatch detected',
            variant: 'destructive',
          });
          return;
        }

        if (data?.instagram_username) {
          const cleanUsername = data.instagram_username.trim();
          
          // Validate that it's not an email address
          const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanUsername);
          if (isEmail) {
            console.error('[InstagramProfileSettings] Invalid data: Email found in instagram_username field:', cleanUsername);
            toast({
              title: t('common.error'),
              description: t('settings.instagram.invalidUsername'),
              variant: 'destructive',
            });
            // Clear the invalid data
            setCurrentUsername('');
            setInstagramUsername('');
            return;
          }

          // Validate Instagram username format
          const isValidFormat = /^[a-zA-Z0-9._]+$/.test(cleanUsername);
          if (!isValidFormat) {
            console.error('[InstagramProfileSettings] Invalid Instagram username format:', cleanUsername);
            toast({
              title: t('common.error'),
              description: t('settings.instagram.invalidUsername'),
              variant: 'destructive',
            });
            setCurrentUsername('');
            setInstagramUsername('');
            return;
          }

          setCurrentUsername(cleanUsername);
          setInstagramUsername(cleanUsername);
          
          // If there was whitespace, update the database to clean it
          if (cleanUsername !== data.instagram_username) {
            supabase
              .from('user_profiles')
              .update({ 
                instagram_username: cleanUsername,
                updated_at: new Date().toISOString()
              })
              .eq('user_id', user.id)
              .then(({ error }) => {
                if (error) {
                  console.error('[InstagramProfileSettings] Error cleaning username:', error);
                } else {
                }
              });
          }
        } else {
        }
      } catch (error) {
        console.error('[InstagramProfileSettings] Error fetching Instagram username:', error);
        toast({
          title: t('common.error'),
          description: t('settings.instagram.fetchError'),
          variant: 'destructive',
        });
      } finally {
        setIsFetching(false);
      }
    };

    fetchProfile();
  }, [user?.id, toast, t]);

  const handleUsernameChange = (value: string) => {
    // Convert to lowercase immediately
    const lowercaseValue = value.toLowerCase();
    setInstagramUsername(lowercaseValue);
    
    const error = validateInstagramInput(lowercaseValue);
    setValidationError(error);
  };

  const handleSave = async () => {
    if (!user?.id) {
      toast({
        title: t('common.error'),
        description: t('settings.instagram.notAuthenticated'),
        variant: 'destructive',
      });
      return;
    }

    const trimmedInput = instagramUsername.trim();

    if (!trimmedInput) {
      toast({
        title: t('common.error'),
        description: t('settings.instagram.enterUsername'),
        variant: 'destructive',
      });
      return;
    }

    // Extract and validate username
    const username = extractInstagramUsername(trimmedInput);
    const error = validateInstagramInput(trimmedInput);
    
    if (error || !username) {
      toast({
        title: t('common.error'),
        description: error || t('settings.instagram.invalidUsername'),
        variant: 'destructive',
      });
      return;
    }

    // Only allow adding if no username exists
    if (currentUsername) {
      toast({
        title: t('settings.instagram.cannotChange'),
        description: t('settings.instagram.cannotChangeDesc'),
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);

    try {
      
      const { error: saveError } = await supabase
        .from('user_profiles')
        .upsert({ 
          user_id: user.id,
          instagram_username: username,
          updated_at: new Date().toISOString()
        }, { 
          onConflict: 'user_id' 
        });

      if (saveError) {
        console.error('[InstagramProfileSettings] Error saving username:', saveError);
        throw saveError;
      }

      setCurrentUsername(username);
      setInstagramUsername(username);
      
      // Trigger profile scraping webhook to fetch Instagram data
      try {
        await supabase.functions.invoke('trigger-profile-scraping', {
          body: { username: username }
        });
      } catch (webhookError) {
        logEdgeFunctionError('trigger-profile-scraping', webhookError, { username });
        // Don't fail the whole save if webhook fails
      }
    } catch (error) {
      console.error('[InstagramProfileSettings] Error saving Instagram username:', error);
      toast({
        title: t('common.error'),
        description: t('settings.instagram.saveError'),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isFetching) {
    return (
      <div className="glass-card rounded-2xl p-6 border border-border/50">
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
              <Instagram className="h-5 w-5 text-primary" />
            </div>
            <h3 className="text-xl font-bold">{t('settings.instagram.title')}</h3>
          </div>
          <p className="text-sm text-muted-foreground ml-12">
            {t('settings.instagram.description')}
          </p>
        </div>
        <div className="ml-12">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`glass-card rounded-2xl p-6 ${!currentUsername ? '!border-2 !border-destructive' : ''}`}>
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Instagram className="h-5 w-5 text-primary" />
          </div>
          <h3 className="text-xl font-bold">{t('settings.instagram.title')}</h3>
        </div>
        <p className="text-sm text-muted-foreground ml-12">
          {t('settings.instagram.description')}
        </p>
      </div>
      <div className="ml-12 space-y-4">
        <div className="space-y-2">
          <Label htmlFor="instagram-username">{t('settings.instagram.usernameLabel')}</Label>
          <div className="relative">
            <div className="flex items-center gap-2">
              <Input
                id="instagram-username"
                type="text"
                placeholder="nazwa_użytkownika lub instagram.com/nazwa"
                value={instagramUsername}
                onChange={(e) => handleUsernameChange(e.target.value)}
                disabled={!!currentUsername || isLoading}
                className={currentUsername ? 'bg-muted flex-1' : 'flex-1'}
              />
              {currentUsername && (
                <div className="flex-shrink-0">
                  <Check className="h-4 w-4 text-success" />
                </div>
              )}
            </div>
          </div>
          {currentUsername && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground mt-2 p-2 bg-muted/50 rounded-md">
              <Lock className="h-4 w-4" />
              <span>{t('settings.instagram.locked')}</span>
            </div>
          )}
          {validationError && !currentUsername && (
            <p className="text-xs text-destructive mt-1">
              {validationError}
            </p>
          )}
          {!currentUsername && !validationError && (
            <p className="text-xs text-muted-foreground mt-1">
              {t('settings.instagram.usernameHint')}
            </p>
          )}
        </div>

        {!currentUsername && (
          <Button
            onClick={handleSave}
            disabled={isLoading || !instagramUsername.trim()}
            className="w-full"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('settings.saving')}
              </>
            ) : (
              t('settings.instagram.saveButton')
            )}
          </Button>
        )}
      </div>
    </div>
  );
};
